import { take, map, switchMap, mergeMap, filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import {
  LoadUnitsAction,
  LoadUnitsCompleteAction,
  ProductUnitActions,
  ProductUnitActionTypes,
  ResetUnitsAction,
  SetActualUnitsAction,
  SetActualUnitsCompleteAction,
} from '../../actions/product-unit.actions';
import { getSpecificationProductUnits, OrderBuilderState } from '../../../../reducers';
import { RESET_BUILDER } from '../../../../actions/order-builder.actions';
import { ProductService, UnitsResponseV2 } from '@cemex-core/cmx-api';
import { UnicEffectsAvailableEnum } from '@ordersnproducts/modules/shared/modules/dynamic-effects/deletable-effect-sources.constant';
import { EffectsRegistrationService } from '@ordersnproducts/modules/shared/modules/dynamic-effects/effects-registration.service';

@Injectable()
export class UnitsEffects {
  loadUnits: Observable<LoadUnitsCompleteAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductUnitActionTypes.LOAD_UNITS),
      filter(() => this.isEffectAvailable()),
      mergeMap((action: LoadUnitsAction) => {
        const payloadProductId = action.payload.productId;
        return this.productService.getUnitsV2(action.payload.request).pipe(
          map((results: UnitsResponseV2) => {
            return new LoadUnitsCompleteAction({
              [payloadProductId]: results.units,
            });
          }),
        );
      }),
    ),
  );

  loadUnitsComplete: Observable<ProductUnitActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductUnitActionTypes.LOAD_UNITS_COMPLETE),
      map((action: LoadUnitsCompleteAction) => {
        const keys = Object.keys(action.payload);
        return new SetActualUnitsAction({
          productId: Number(keys.pop()),
        });
      }),
    ),
  );

  setActiveUnits: Observable<ProductUnitActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductUnitActionTypes.SET_ACTUAL_UNITS),
      switchMap((action: SetActualUnitsAction) => {
        return this.store
          .select(getSpecificationProductUnits)
          .pipe(take(1))
          .pipe(
            map((state) => state.productUnits),
            switchMap((stateUnits) => {
              return of(new SetActualUnitsCompleteAction(stateUnits[action.payload.productId]));
            }),
          );
      }),
    ),
  );

  unitsReset$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(RESET_BUILDER),
      filter(() => this.isEffectAvailable()),
      map(() => new ResetUnitsAction()),
    ),
  );

  /**
   * Identifies if the effect is available to be executed
   * @returns {boolean}
   */
  private isEffectAvailable(): boolean {
    return this.effectRegister.isEffectAvailable(UnicEffectsAvailableEnum.UnitsEffects);
  }

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private productService: ProductService,
    private effectRegister: EffectsRegistrationService,
  ) {}
}
